import { createServerFn } from "@tanstack/react-start";
import { z } from "zod";
import { supabaseAdmin } from "@/integrations/supabase/client.server";

const slugSchema = z.object({ slug: z.string().min(1).max(120) });

export type HostingPageMeta = {
  slug: string;
  title: string;
  subtitle: string | null;
  seo_title: string | null;
  seo_description: string | null;
  og_image_url: string | null;
  hero_image_url: string | null;
  hero_eyebrow: string | null;
} | null;

/**
 * Public, unauthenticated fetch of a hosting page's meta fields.
 * Uses the admin client on the server (never exposed to browser) to
 * bypass any anon Data API JWT issues, but only returns published rows.
 */
export const getHostingPageMeta = createServerFn({ method: "GET" })
  .inputValidator((data: unknown) => slugSchema.parse(data))
  .handler(async ({ data }): Promise<HostingPageMeta> => {
    const { data: row, error } = await supabaseAdmin
      .from("hosting_pages")
      .select("slug,title,subtitle,seo_title,seo_description,og_image_url,hero_image_url,hero_eyebrow,status,enabled")
      .eq("slug", data.slug)
      .maybeSingle();
    if (error || !row) return null;
    if (!row.enabled || row.status !== "published") return null;
    return {
      slug: row.slug,
      title: row.title,
      subtitle: row.subtitle,
      seo_title: row.seo_title,
      seo_description: row.seo_description,
      og_image_url: row.og_image_url,
      hero_image_url: row.hero_image_url,
      hero_eyebrow: row.hero_eyebrow,
    };
  });
